import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  Text,
} from '@chakra-ui/react';
import { useUser } from '@clerk/nextjs';
import { labDocumentsMolecule } from '@web-app/atoms/in-session/labDocumentsMolecule';
import { LabDocumentPreview } from '@web-app/components/LabDocuments/LabDocumentPreview';
import { LabDocumentTable } from '@web-app/components/LabDocuments/LabDocumentTable';
import { EVENT_NAMES, useAnalytics } from '@web-app/context/AnalyticsContext';
import { usePresignedDocumentDownload } from '@web-app/hooks/usePresignedDocumentDownload';
import { useMolecule } from 'bunshi/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { FC, useCallback, useEffect, useLayoutEffect, useRef } from 'react';

// oxlint-disable-next-line @typescript-eslint/no-empty-object-type
interface LabDocumentsTabProps {}

/**
 * LabDocumentsTab component displays a list of lab documents related to the current call,
 * allows the user to select a document to preview, and provides options to download or open the document in a new tab.
 */
export const LabDocumentsTab: FC<LabDocumentsTabProps> = () => {
  const { trackEvent } = useAnalytics();

  const {
    showAllDocumentsAtom,
    viewerIdAtom,
    labDocumentsQueryAtom,
    labDocumentsAtom,
    labDocumentsIsLoadingAtom,
    labDocumentsErrorAtom,
    currentLabDocumentIdAtom,
    currentLabDocumentAtom,
    labDocumentDownloadUrlAtom,
    labDocumentDownloadUrlIsLoadingAtom,
    labDocumentDownloadUrlExpiresAtAtom,
    refetchLabDocumentDownloadUrlAtom,
  } = useMolecule(labDocumentsMolecule);

  const { user, isLoaded } = useUser();
  const expectedViewerId = isLoaded ? (user?.id ?? '') : undefined;
  const [storedViewerId, setViewerId] = useAtom(viewerIdAtom);
  const viewerReady = isLoaded && storedViewerId === expectedViewerId;

  const [showAllDocuments, setShowAllDocuments] = useAtom(showAllDocumentsAtom);
  const { refetch: refetchDocuments } = useAtomValue(labDocumentsQueryAtom);
  const loadedDocuments = useAtomValue(labDocumentsAtom);
  const queryIsLoading = useAtomValue(labDocumentsIsLoadingAtom);
  const queryError = useAtomValue(labDocumentsErrorAtom);
  const documents = viewerReady ? loadedDocuments : [];
  const isLoading = !viewerReady || queryIsLoading;
  const error = viewerReady ? queryError : undefined;
  const currentLabDocumentId = useAtomValue(currentLabDocumentIdAtom);
  const currentLabDocument = useAtomValue(currentLabDocumentAtom);
  const downloadUrl = useAtomValue(labDocumentDownloadUrlAtom);
  const isDownloadUrlLoading = useAtomValue(
    labDocumentDownloadUrlIsLoadingAtom,
  );
  const expiresAt = useAtomValue(labDocumentDownloadUrlExpiresAtAtom);
  const refetchDownloadUrl = useAtomValue(refetchLabDocumentDownloadUrlAtom);
  const setCurrentLabDocumentId = useSetAtom(currentLabDocumentIdAtom);

  const previousViewerId = useRef(expectedViewerId);
  // Copy Clerk into the session atom before paint so the list query key
  // changes with the signed-in user. Hide the previous viewer's rows until
  // that copy lands. Close an open preview only when the viewer actually
  // changes, so remounting this tab does not discard the current document.
  useLayoutEffect(
    function syncLabDocumentsViewerId() {
      setViewerId(expectedViewerId);
      if (previousViewerId.current !== expectedViewerId) {
        setCurrentLabDocumentId(null);
        previousViewerId.current = expectedViewerId;
      }
    },
    [expectedViewerId, setViewerId, setCurrentLabDocumentId],
  );

  useEffect(
    function trackLabsTabViewed() {
      trackEvent(EVENT_NAMES.LABS_TAB_VIEWED);
    },
    [trackEvent],
  );

  const onDocumentClick = useCallback(
    (documentId: string) => {
      setCurrentLabDocumentId(documentId);
      trackEvent(EVENT_NAMES.LAB_DOCUMENT_DRAWER_OPENED, {
        labDocumentId: documentId,
      });
    },
    [setCurrentLabDocumentId, trackEvent],
  );

  const onDismiss = useCallback(() => {
    setCurrentLabDocumentId(null);
    trackEvent(EVENT_NAMES.LAB_DOCUMENT_DRAWER_CLOSED, {
      labDocumentId: currentLabDocumentId,
      filename: currentLabDocument?.filename,
    });
  }, [
    setCurrentLabDocumentId,
    trackEvent,
    currentLabDocumentId,
    currentLabDocument?.filename,
  ]);

  const { downloadDocument, openInNewTab } = usePresignedDocumentDownload({
    downloadUrl,
    expiresAt,
    filename: currentLabDocument?.filename,
    refetchDownloadUrl,
  });

  const onDownload = useCallback(async () => {
    trackEvent(EVENT_NAMES.LAB_DOCUMENT_DOWNLOAD_BUTTON_CLICKED, {
      labDocumentId: currentLabDocumentId,
      filename: currentLabDocument?.filename,
    });
    await downloadDocument();
  }, [
    downloadDocument,
    currentLabDocument?.filename,
    currentLabDocumentId,
    trackEvent,
  ]);

  const onOpenInNewTab = useCallback(async () => {
    trackEvent(EVENT_NAMES.LAB_DOCUMENT_OPENED_IN_NEW_TAB, {
      labDocumentId: currentLabDocumentId,
      filename: currentLabDocument?.filename,
    });
    await openInNewTab();
  }, [
    openInNewTab,
    currentLabDocumentId,
    currentLabDocument?.filename,
    trackEvent,
  ]);

  return (
    <>
      <Box px={3} py={3}>
        {showAllDocuments && (
          <Text fontSize="sm" fontWeight={500} mb={2}>
            All patient documents
          </Text>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setCurrentLabDocumentId(null);
            setShowAllDocuments(!showAllDocuments);
          }}
        >
          {showAllDocuments ? 'Show labs only' : 'View all patient documents'}
        </Button>
      </Box>
      {error ? (
        <Box p={4}>
          <Text color="text.error" mb={3}>
            {showAllDocuments
              ? 'Failed to load patient documents.'
              : 'Failed to load lab documents.'}
          </Text>
          <Button size="sm" onClick={() => void refetchDocuments()}>
            Retry
          </Button>
        </Box>
      ) : (
        <LabDocumentTable
          documents={documents}
          onDocumentClick={onDocumentClick}
          isLoading={isLoading}
          emptyMessage={
            showAllDocuments ? 'No patient documents found' : undefined
          }
        />
      )}
      <Drawer
        isOpen={currentLabDocumentId !== null}
        onClose={onDismiss}
        placement="left"
        size="xl"
        trapFocus={false}
        blockScrollOnMount={false}
        autoFocus={false}
      >
        <DrawerContent>
          <DrawerBody p={0}>
            <LabDocumentPreview
              filename={currentLabDocument?.filename ?? ''}
              mimeType={currentLabDocument?.mimeType ?? ''}
              downloadUrl={downloadUrl}
              isLoadingUrl={isDownloadUrlLoading}
              onDismiss={onDismiss}
              onDownload={onDownload}
              onOpenInNewTab={onOpenInNewTab}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

LabDocumentsTab.displayName = 'LabDocumentsTab';
